import React, { useState } from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { SimpleChat } from './SimpleChat';

const MODERN_COLORS = {
  background: '#FFFFFF',
  text: '#0A0A0A',
  textSecondary: '#6B7280',
  primary: '#0A0A0A',
  primaryHover: '#1F1F1F',
  border: '#E5E7EB',
  accent: '#10B981',
};

export const MinimalistLanding: React.FC = () => {
  const [showChat, setShowChat] = useState(false);

  if (showChat) {
    return <SimpleChat />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: MODERN_COLORS.background,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle gradient background */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '50%',
          height: '100%',
          background: 'radial-gradient(circle at top right, rgba(16, 185, 129, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <Box sx={{ textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
            {/* Small accent */}
            <Box
              sx={{
                display: 'inline-block',
                px: 2,
                py: 0.5,
                mb: 3,
                borderRadius: 20,
                bgcolor: MODERN_COLORS.accent,
                color: 'white',
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 500, letterSpacing: 0.5 }}>
                LIVE DEMO
              </Typography>
            </Box>

            {/* Main headline */}
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                color: MODERN_COLORS.text,
                mb: 2,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              Customer support that
              <br />
              actually{' '}
              <Box component="span" sx={{ color: MODERN_COLORS.accent }}>
                understands
              </Box>
            </Typography>

            {/* Subheadline */}
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.1rem', md: '1.3rem' },
                color: MODERN_COLORS.textSecondary,
                mb: 5,
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              Experience AI-powered support that feels human.
              <br />
              No scripts, no waiting, just help when you need it.
            </Typography>

            {/* CTA Button */}
            <Button
              variant="contained"
              size="large"
              onClick={() => setShowChat(true)}
              endIcon={<ArrowRight size={20} />}
              sx={{
                bgcolor: MODERN_COLORS.primary,
                color: 'white',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 500,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: MODERN_COLORS.primaryHover,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Try it now
            </Button>

            {/* Sample conversation */}
            <Box sx={{ mt: 6 }}>
              <Box
                sx={{
                  maxWidth: 400,
                  mx: 'auto',
                  p: 3,
                  borderRadius: 2,
                  border: `1px solid ${MODERN_COLORS.border}`,
                  bgcolor: '#FAFAFA',
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: MODERN_COLORS.primary,
                      color: 'white',
                      fontSize: '0.875rem',
                      ml: 'auto',
                      float: 'right',
                    }}
                  >
                    Where's my order?
                  </Box>
                  <Box sx={{ clear: 'both' }} />
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: '#F3F4F6',
                      color: MODERN_COLORS.text,
                      fontSize: '0.875rem',
                      maxWidth: '80%',
                    }}
                  >
                    Let me check that for you! 🔍 Your order from KFC is with Juan and will arrive in 10 minutes.
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Trust indicators */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 6, opacity: 0.7 }}>
              <Typography
                variant="caption"
                sx={{
                  color: MODERN_COLORS.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>&lt;3s</Box> response time
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: MODERN_COLORS.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>95%</Box> resolution rate
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: MODERN_COLORS.textSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>24/7</Box> available
              </Typography>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};